<template>
	<main class="curtain-page">
		<section class="studio" :aria-label="t.previewLabel" @pointerdown="dismissRotateHint()" @wheel="dismissRotateHint()">
			<div ref="studioMount" class="studio__mount"></div>
			<div class="studio__readout">
				<strong>{{ form.width }} x {{ form.height }} cm</strong>
				<span>{{ mountText }} / {{ controlText }} / {{ t.drop }} {{ form.dropPercent }}%</span>
			</div>
			<button type="button" class="rotate-hint" @click.stop="resetStudioView" @pointerdown.stop>
				<div class="rotate-hint__icon">↺</div>
				<div>
					<strong>{{ t.rotateHint }}</strong>
					<span>{{ t.rotateHintDesc }}</span>
				</div>
			</button>
		</section>

		<aside class="customizer" :aria-label="t.optionLabel">
			<div class="customizer__body">
				<header class="customizer__header customizer__header--lang">
					<div>
						<p>{{ t.madeToMeasure }}</p>
						<h1>{{ t.title }}</h1>
					</div>

					<button type="button" class="lang-toggle" @click="toggleLang">
						{{ langButtonText }}
					</button>
				</header>

				<section class="control-section">
					<div class="control-row">
						<label for="curtain-width">{{ t.width }}</label>
						<output>{{ form.width }} cm</output>
					</div>
					<div class="dimension-control">
						<input id="curtain-width" :value="form.width" class="range" type="range" min="120" max="420" step="1" @input="setDimension('width', $event.target.value)" />
						<input
							:value="form.width"
							class="number-input"
							type="number"
							min="120"
							max="420"
							step="1"
							:aria-label="t.widthAria"
							@input="setDimension('width', $event.target.value)"
						/>
					</div>
				</section>

				<section class="control-section">
					<div class="control-row">
						<label for="curtain-height">{{ t.height }}</label>
						<output>{{ form.height }} cm</output>
					</div>
					<div class="dimension-control">
						<input id="curtain-height" :value="form.height" class="range" type="range" min="120" max="360" step="1" @input="setDimension('height', $event.target.value)" />
						<input
							:value="form.height"
							class="number-input"
							type="number"
							min="120"
							max="360"
							step="1"
							:aria-label="t.heightAria"
							@input="setDimension('height', $event.target.value)"
						/>
					</div>
				</section>

				<section class="control-section">
					<div class="control-title">{{ t.mountType }}</div>
					<div class="segmented">
						<button
							v-for="option in mountOptions"
							:key="option.value"
							type="button"
							:aria-pressed="form.mount === option.value"
							:class="{ active: form.mount === option.value }"
							@click="form.mount = option.value"
						>
							<span>{{ mountOptionName(option.value) }}</span>
							<small>{{ mountOptionNote(option.value) }}</small>
						</button>
					</div>
				</section>

				<section class="control-section">
					<div class="control-title">{{ t.controlType }}</div>
					<div class="segmented">
						<button
							v-for="option in controlOptions"
							:key="option.value"
							type="button"
							:aria-pressed="form.control === option.value"
							:class="{ active: form.control === option.value }"
							@click="form.control = option.value"
						>
							<span>{{ controlOptionName(option.value) }}</span>
							<small>{{ controlOptionNote(option.value) }}</small>
						</button>
					</div>
				</section>

				<section class="control-section">
					<div class="control-title">{{ t.wallColor }}</div>
					<div class="swatches">
						<button
							v-for="option in wallColors"
							:key="option.value"
							type="button"
							:title="colorName(option)"
							:aria-label="colorName(option)"
							:class="{ active: form.wallColor === option.value }"
							:style="{ backgroundColor: option.value }"
							@click="form.wallColor = option.value"
						></button>
					</div>
					<p class="selected-name">{{ selectedWallName }}</p>
				</section>

				<section class="control-section">
					<div class="control-title">{{ t.curtainColor }}</div>
					<div class="swatches">
						<button
							v-for="option in curtainColors"
							:key="option.value"
							type="button"
							:title="colorName(option)"
							:aria-label="colorName(option)"
							:class="{ active: form.curtainColor === option.value }"
							:style="{ backgroundColor: option.value }"
							@click="form.curtainColor = option.value"
						></button>
					</div>
					<p class="selected-name">{{ selectedCurtainName }}</p>
				</section>

				<section class="control-section">
					<div class="control-row">
						<label for="curtain-drop">{{ t.dropPercent }}</label>
						<output>{{ form.dropPercent }}%</output>
					</div>
					<input id="curtain-drop" :value="form.dropPercent" class="range" type="range" min="0" max="100" step="1" @input="setDrop($event.target.value)" />
				</section>
			</div>

			<footer class="checkout">
				<div class="checkout__top">
					<div class="checkout__label">
						<span>{{ t.total }}</span>
						<button type="button" class="detail-toggle" @click="showBreakdown = !showBreakdown">
							{{ t.detail }}
						</button>
					</div>
					<strong>{{ currency(totalPrice) }}</strong>
				</div>

				<transition name="breakdown">
					<div v-if="showBreakdown" class="breakdown-popover" :aria-label="t.priceDetail">
						<div class="breakdown-popover__header">
							<strong>{{ t.priceDetail }}</strong>
							<button type="button" :aria-label="t.closeDetail" @click="showBreakdown = false">
								{{ t.close }}
							</button>
						</div>
						<div class="breakdown">
							<div v-for="line in priceLines" :key="line.label">
								<span>{{ line.label }}</span>
								<strong>{{ currency(line.amount) }}</strong>
							</div>
						</div>
					</div>
				</transition>

				<button class="purchase-button" type="button" :disabled="cartBusy" @click="buyNow">
					{{ cartBusy ? t.processing : t.buy }}
				</button>

				<p v-if="cartMessage" class="cart-message">{{ cartMessage }}</p>
			</footer>
		</aside>
	</main>
</template>

<script>
import { createCurtainStudio } from '../three/curtainStudio';

const DIMENSIONS = {
	width: { min: 120, max: 420 },
	height: { min: 120, max: 360 }
};

const I18N = {
	zh: {
		previewLabel: '窗帘定制预览',
		optionLabel: '窗帘定制选项',
		madeToMeasure: 'Made to measure',
		title: '窗帘定制',
		width: '窗帘宽度',
		widthAria: '窗帘宽度厘米',
		height: '窗帘高度',
		heightAria: '窗帘高度厘米',
		mountType: '安装方式',
		controlType: '控制方式',
		wallColor: '墙体颜色',
		curtainColor: '帘布颜色',
		dropPercent: '窗帘下拉比例',
		drop: '下拉',
		total: '总价',
		detail: '明细',
		priceDetail: '价格明细',
		close: '关闭',
		closeDetail: '关闭明细',
		buy: '购买',
		processing: '处理中',
		inside: '内装',
		insideDesc: '窗洞内侧',
		outside: '外装',
		outsideDesc: '覆盖墙面',
		motor: '电动',
		motorDesc: '电机端盖',
		manual: '手动',
		manualDesc: '珠链控制',
		baseCustom: '基础定制',
		fabric: '帘布',
		addedCart: '已加入购物车。',
		noVariant: '请先配置 VUE_APP_SHOPIFY_VARIANT_ID，再接入 Shopify 购买。',
		buyFailed: '当前环境无法完成购买，请在 Shopify 店铺页验证。',
		rotateHint: '拖动可旋转',
		rotateHintDesc: '拖动 3D 窗帘查看不同角度'
	},
	en: {
		previewLabel: 'Curtain preview',
		optionLabel: 'Curtain customizer',
		madeToMeasure: 'Made to measure',
		title: 'Custom Curtain',
		width: 'Curtain Width',
		widthAria: 'Curtain width in centimeters',
		height: 'Curtain Height',
		heightAria: 'Curtain height in centimeters',
		mountType: 'Mount Type',
		controlType: 'Control Type',
		wallColor: 'Wall Color',
		curtainColor: 'Fabric Color',
		dropPercent: 'Curtain Drop',
		drop: 'Drop',
		total: 'Total',
		detail: 'Details',
		priceDetail: 'Price Details',
		close: 'Close',
		closeDetail: 'Close details',
		buy: 'Buy',
		processing: 'Processing',
		inside: 'Inside Mount',
		insideDesc: 'Inside window recess',
		outside: 'Outside Mount',
		outsideDesc: 'Cover wall surface',
		motor: 'Motorized',
		motorDesc: 'Motor cover',
		manual: 'Manual',
		manualDesc: 'Chain control',
		baseCustom: 'Base Custom',
		fabric: 'Fabric',
		addedCart: 'Added to cart.',
		noVariant: 'Please configure VUE_APP_SHOPIFY_VARIANT_ID before connecting Shopify checkout.',
		buyFailed: 'Checkout is unavailable in the current environment. Please verify it on the Shopify store page.',
		rotateHint: 'Drag to rotate',
		rotateHintDesc: 'View the curtain from different angles'
	}
};

const CNY_TO_USD = 0.147806;

export default {
	name: 'CurtainConfigurator',
	data() {
		return {
			rotateHintDismissed: false,
			lang: localStorage.getItem('curtain_lang') || 'zh',
			studio: null,
			cartBusy: false,
			cartMessage: '',
			showBreakdown: false,
			form: {
				width: 280,
				height: 220,
				mount: 'inside',
				control: 'motor',
				wallColor: '#d1d2ce',
				curtainColor: '#f4f1ea',
				dropPercent: 72
			},
			mountOptions: [{ value: 'inside' }, { value: 'outside' }],
			controlOptions: [{ value: 'motor' }, { value: 'manual' }],
			wallColors: [
				{ nameZh: '哑光灰白', nameEn: 'Matte Gray White', value: '#d1d2ce' },
				{ nameZh: '暖砂灰', nameEn: 'Warm Sand Gray', value: '#d8d0c3' },
				{ nameZh: '雾面白', nameEn: 'Matte White', value: '#ecebe6' },
				{ nameZh: '浅鼠尾草', nameEn: 'Light Sage', value: '#cbd6cc' },
				{ nameZh: '冷调水泥', nameEn: 'Cool Concrete', value: '#c7ccd0' },
				{ nameZh: '陶土灰', nameEn: 'Clay Gray', value: '#b9aaa0' }
			],
			curtainColors: [
				{ nameZh: '雾白纱', nameEn: 'Mist White Sheer', value: '#f4f1ea' },
				{ nameZh: '亚麻米', nameEn: 'Linen Beige', value: '#ded0b8' },
				{ nameZh: '浅石灰', nameEn: 'Light Stone', value: '#c6c9c4' },
				{ nameZh: '烟青色', nameEn: 'Smoky Sage', value: '#7d9186' },
				{ nameZh: '深海蓝', nameEn: 'Deep Sea Blue', value: '#405772' },
				{ nameZh: '陶棕色', nameEn: 'Terracotta Brown', value: '#a57658' }
			]
		};
	},
	computed: {
		t() {
			return I18N[this.lang] || I18N.zh;
		},
		langButtonText() {
			return this.lang === 'zh' ? 'EN' : '中文';
		},
		selectedWall() {
			return this.findOption(this.wallColors, this.form.wallColor);
		},
		selectedCurtain() {
			return this.findOption(this.curtainColors, this.form.curtainColor);
		},
		selectedWallName() {
			return this.colorName(this.selectedWall);
		},
		selectedCurtainName() {
			return this.colorName(this.selectedCurtain);
		},
		mountText() {
			return this.form.mount === 'inside' ? this.t.inside : this.t.outside;
		},
		controlText() {
			return this.form.control === 'motor' ? this.t.motor : this.t.manual;
		},
		area() {
			return (this.form.width * this.form.height) / 10000;
		},
		priceLines() {
			const base = 299;
			const fabric = Math.round(this.area * 238);
			const control = this.form.control === 'motor' ? 420 : 90;
			const mount = this.form.mount === 'outside' ? 120 : 0;

			return [
				{ label: this.t.baseCustom, amount: base },
				{ label: `${this.t.fabric} ${this.area.toFixed(2)} m²`, amount: fabric },
				{ label: this.controlText, amount: control },
				{ label: this.mountText, amount: mount }
			];
		},
		totalPrice() {
			return this.priceLines.reduce((sum, line) => sum + line.amount, 0);
		},
		variantId() {
			return process.env.VUE_APP_SHOPIFY_VARIANT_ID || '';
		}
	},
	watch: {
		form: {
			deep: true,
			handler() {
				this.cartMessage = '';
				this.syncStudio();
			}
		}
	},
	mounted() {
		document.title = this.t.title;
		this.studio = createCurtainStudio(this.$refs.studioMount);
		this.syncStudio();
	},
	beforeDestroy() {
		if (this.studio) {
			this.studio.dispose();
		}
	},
	methods: {
		resetStudioView() {
			console.log('回正');
			if (this.studio && this.studio.resetView) {
				this.studio.resetView();
			}
		},
		dismissRotateHint() {
			if (this.rotateHintDismissed) return;

			this.rotateHintDismissed = true;
		},
		toggleLang() {
			this.lang = this.lang === 'zh' ? 'en' : 'zh';
			localStorage.setItem('curtain_lang', this.lang);
			document.title = this.t.title;
		},
		mountOptionName(value) {
			return value === 'inside' ? this.t.inside : this.t.outside;
		},
		mountOptionNote(value) {
			return value === 'inside' ? this.t.insideDesc : this.t.outsideDesc;
		},
		controlOptionName(value) {
			return value === 'motor' ? this.t.motor : this.t.manual;
		},
		controlOptionNote(value) {
			return value === 'motor' ? this.t.motorDesc : this.t.manualDesc;
		},
		colorName(option) {
			if (!option) return '';
			return this.lang === 'zh' ? option.nameZh : option.nameEn;
		},
		syncStudio() {
			if (this.studio) {
				this.studio.update(this.form);
			}
		},
		setDimension(key, value) {
			const bounds = DIMENSIONS[key];
			const next = Math.round(Number(value));

			if (!Number.isFinite(next)) return;

			this.form[key] = Math.min(bounds.max, Math.max(bounds.min, next));
		},
		setDrop(value) {
			const next = Math.round(Number(value));

			if (!Number.isFinite(next)) return;

			this.form.dropPercent = Math.min(100, Math.max(0, next));
		},
		findOption(options, value) {
			return options.find((option) => option.value === value) || options[0];
		},
		// currency(value) {
		// 	const symbol = this.lang === 'zh' ? '¥' : '$';
		// 	const amount = Math.round(Number(value) || 0).toLocaleString('en-US');

		// 	return `${symbol}${amount}`;
		// },
		currency(value) {
			const rawValue = Number(value) || 0;

			if (this.lang === 'zh') {
				return `¥${Math.round(rawValue).toLocaleString('en-US')}`;
			}

			const usdValue = rawValue * CNY_TO_USD;
			return `$${Math.round(usdValue).toLocaleString('en-US')}`;
		},
		async buyNow() {
			this.cartMessage = '';

			if (!this.variantId) {
				this.cartMessage = this.t.noVariant;
				return;
			}

			this.cartBusy = true;

			try {
				const response = await fetch('/cart/add.js', {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						Accept: 'application/json'
					},
					body: JSON.stringify({
						id: Number(this.variantId),
						quantity: 1,
						properties: {
							窗帘宽度: `${this.form.width} cm`,
							窗帘高度: `${this.form.height} cm`,
							安装方式: this.mountText,
							控制方式: this.controlText,
							墙体颜色: this.selectedWallName,
							帘布颜色: this.selectedCurtainName,
							下拉比例: `${this.form.dropPercent}%`,
							预估总价: this.currency(this.totalPrice)
						}
					})
				});

				if (!response.ok) {
					throw new Error(`Shopify add failed: ${response.status}`);
				}

				this.cartMessage = this.t.addedCart;
			} catch (error) {
				this.cartMessage = this.t.buyFailed;
			} finally {
				this.cartBusy = false;
			}
		}
	}
};
</script>

<style scoped>
.studio {
	position: relative;
	cursor: grab;
}

.studio:active {
	cursor: grabbing;
}

.rotate-hint {
	position: absolute;
	left: 50%;
	top: 28px;
	z-index: 20;
	transform: translateX(-50%);
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 10px 14px;
	border: 1px solid rgba(255, 255, 255, 0.58);
	border-radius: 999px;
	background: rgba(20, 24, 22, 0.72);
	color: #fff;
	box-shadow: 0 14px 36px rgba(0, 0, 0, 0.18);
	backdrop-filter: blur(10px);

	/* 这里必须改 */
	pointer-events: auto;
	cursor: pointer;
}

.rotate-hint__icon {
	width: 30px;
	height: 30px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgba(255, 255, 255, 0.16);
	font-size: 20px;
	font-weight: 800;
}

.rotate-hint strong {
	display: block;
	font-size: 13px;
	line-height: 1.2;
}

.rotate-hint span {
	display: block;
	margin-top: 2px;
	font-size: 12px;
	line-height: 1.2;
	opacity: 0.78;
}

.curtain-page {
	display: grid;
	height: 100vh;
	min-height: 100vh;
	grid-template-columns: minmax(0, 1fr) minmax(360px, 420px);
	overflow: hidden;
	background: #e3e2dc;
	color: #171918;
}

.studio {
	position: relative;
	min-width: 0;
	min-height: 0;
	height: 100vh;
	overflow: hidden;
	background: #d1d2ce;
}

.studio__mount {
	width: 100%;
	height: 100%;
}

.studio__mount canvas {
	display: block;
	width: 100%;
	height: 100%;
}

.studio__readout {
	position: absolute;
	left: 22px;
	bottom: 22px;
	display: grid;
	gap: 4px;
	max-width: min(320px, calc(100% - 44px));
	padding: 12px 14px;
	border: 1px solid rgba(255, 255, 255, 0.72);
	border-radius: 8px;
	background: rgba(246, 246, 241, 0.78);
	box-shadow: 0 18px 44px rgba(19, 21, 19, 0.16);
	backdrop-filter: blur(12px);
}

.studio__readout strong {
	font-size: 15px;
	line-height: 1.2;
}

.studio__readout span {
	color: #565d58;
	font-size: 13px;
	line-height: 1.35;
}

.customizer {
	display: flex;
	min-height: 0;
	height: 100vh;
	flex-direction: column;
	border-left: 1px solid rgba(24, 28, 26, 0.12);
	background: #faf9f5;
	box-shadow: -18px 0 42px rgba(24, 28, 26, 0.08);
}

.customizer__body {
	flex: 1;
	min-height: 0;
	overflow: auto;
	padding: 30px 28px 24px;
}

.customizer__header {
	margin-bottom: 26px;
}

.customizer__header--lang {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 16px;
}

.customizer__header p {
	margin: 0 0 8px;
	color: #7d684f;
	font-size: 12px;
	font-weight: 700;
	letter-spacing: 0;
	text-transform: uppercase;
}

.customizer__header h1 {
	margin: 0;
	font-size: 29px;
	line-height: 1.12;
}

.lang-toggle {
	flex: 0 0 auto;
	height: 32px;
	padding: 0 13px;
	border: 1px solid #d5d2c9;
	border-radius: 999px;
	background: #ffffff;
	color: #1f2421;
	cursor: pointer;
	font-size: 13px;
	font-weight: 800;
}

.lang-toggle:hover {
	border-color: #202522;
}

.control-section {
	padding: 21px 0;
	border-top: 1px solid #e3e2dc;
}

.control-row,
.control-title {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	margin-bottom: 12px;
	color: #242624;
	font-size: 14px;
	font-weight: 700;
}

output {
	color: #66706a;
	font-size: 14px;
	font-weight: 600;
}

.dimension-control {
	display: grid;
	grid-template-columns: minmax(0, 1fr) 78px;
	gap: 12px;
	align-items: center;
}

.range {
	width: 100%;
	accent-color: #202522;
}

.number-input {
	width: 100%;
	height: 38px;
	border: 1px solid #d6d3ca;
	border-radius: 8px;
	background: #ffffff;
	color: #202522;
	font: inherit;
	text-align: center;
}

.number-input:focus,
.segmented button:focus,
.swatches button:focus,
.detail-toggle:focus,
.lang-toggle:focus,
.purchase-button:focus {
	outline: 2px solid #61796d;
	outline-offset: 2px;
}

.segmented {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 10px;
}

.segmented button {
	min-height: 68px;
	padding: 12px;
	border: 1px solid #d8d6ce;
	border-radius: 8px;
	background: #ffffff;
	color: #222522;
	cursor: pointer;
	text-align: left;
	transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.segmented button:hover {
	transform: translateY(-1px);
}

.segmented button.active {
	border-color: #202522;
	box-shadow: inset 0 0 0 1px #202522;
}

.segmented span,
.segmented small {
	display: block;
}

.segmented span {
	font-size: 14px;
	font-weight: 800;
}

.segmented small {
	margin-top: 5px;
	color: #68716c;
	font-size: 12px;
	line-height: 1.35;
}

.swatches {
	display: grid;
	grid-template-columns: repeat(6, 34px);
	gap: 11px;
}

.swatches button {
	width: 34px;
	height: 34px;
	border: 1px solid #c8c6bd;
	border-radius: 50%;
	box-shadow: inset 0 0 0 2px rgba(255, 255, 255, 0.72);
	cursor: pointer;
}

.swatches button.active {
	border-color: #202522;
	box-shadow: 0 0 0 2px #202522, inset 0 0 0 2px rgba(255, 255, 255, 0.82);
}

.selected-name {
	margin: 10px 0 0;
	color: #66706a;
	font-size: 13px;
	line-height: 1.35;
}

.checkout {
	flex: 0 0 auto;
	position: relative;
	border-top: 1px solid #e1dfd7;
	background: #ffffff;
	padding: 18px 28px 24px;
	box-shadow: 0 -16px 42px rgba(28, 31, 29, 0.08);
}

.checkout__top {
	display: flex;
	align-items: flex-end;
	justify-content: space-between;
	gap: 16px;
	margin-bottom: 13px;
}

.checkout__label {
	display: flex;
	align-items: center;
	gap: 10px;
}

.checkout__label span {
	color: #5e6862;
	font-size: 14px;
	font-weight: 700;
}

.detail-toggle {
	height: 30px;
	padding: 0 12px;
	border: 1px solid #d5d2c9;
	border-radius: 8px;
	background: #f7f6f1;
	color: #232724;
	cursor: pointer;
	font-size: 13px;
	font-weight: 800;
}

.checkout__top strong {
	color: #171918;
	font-size: 31px;
	line-height: 1;
}

.breakdown-popover {
	position: absolute;
	right: 18px;
	bottom: calc(100% - 8px);
	left: 18px;
	z-index: 5;
	padding: 16px 18px;
	border: 1px solid #dedbd3;
	border-radius: 8px;
	background: rgba(255, 255, 255, 0.98);
	box-shadow: 0 -18px 46px rgba(20, 24, 21, 0.16);
}

.breakdown-popover__header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	margin-bottom: 12px;
}

.breakdown-popover__header strong {
	color: #1f2421;
	font-size: 15px;
}

.breakdown-popover__header button {
	border: 0;
	background: transparent;
	color: #68716c;
	cursor: pointer;
	font-size: 13px;
	font-weight: 700;
}

.breakdown {
	display: grid;
	gap: 7px;
	padding: 12px 0 0;
	border-top: 1px solid #eeece5;
}

.breakdown div {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 14px;
	color: #626b65;
	font-size: 13px;
	line-height: 1.35;
}

.breakdown strong {
	color: #252826;
	font-weight: 700;
}

.breakdown-enter-active,
.breakdown-leave-active {
	transition: opacity 0.18s ease, transform 0.18s ease;
}

.breakdown-enter,
.breakdown-leave-to {
	opacity: 0;
	transform: translateY(12px);
}

.purchase-button {
	width: 100%;
	min-height: 49px;
	border: 0;
	border-radius: 8px;
	background: #1b211e;
	color: #ffffff;
	cursor: pointer;
	font-size: 16px;
	font-weight: 800;
}

.purchase-button:disabled {
	cursor: wait;
	opacity: 0.62;
}

.cart-message {
	margin: 11px 0 0;
	color: #657267;
	font-size: 13px;
	line-height: 1.4;
}

@media (max-width: 980px) {
	.curtain-page {
		height: auto;
		grid-template-columns: 1fr;
		overflow: visible;
	}

	.studio {
		height: 58vh;
		min-height: 58vh;
	}

	.customizer {
		height: auto;
		min-height: auto;
		border-left: 0;
		border-top: 1px solid rgba(24, 28, 26, 0.12);
	}

	.customizer__body {
		overflow: visible;
	}
}

@media (max-width: 520px) {
	.customizer__body,
	.checkout {
		padding-right: 18px;
		padding-left: 18px;
	}

	.customizer__header h1 {
		font-size: 25px;
	}

	.dimension-control {
		grid-template-columns: 1fr;
	}

	.number-input {
		max-width: 118px;
	}

	.swatches {
		grid-template-columns: repeat(6, 32px);
		gap: 9px;
	}

	.swatches button {
		width: 32px;
		height: 32px;
	}
}
</style>
